import type { OnDestroy, OnInit } from '@angular/core';
import { Directive, ElementRef, inject, input, output } from '@angular/core';

/**
 * Директива для обработки свайпов на элементе
 * Использует нативные события touch для обработки свайпов
 * Автоматически очищает слушатели событий при уничтожении директивы
 */
@Directive({
  selector: '[libSwipe]',
  exportAs: 'libSwipe',
})
export class SwipeDirective implements OnInit, OnDestroy {
  /** Минимальное расстояние для определения свайпа */
  readonly swipeThreshold = input(50);
  /** Игнорировать свайпы при скролле дочерних элементов */
  readonly ignoreChildrenScroll = input(true);

  readonly swipeLeft = output<void>();
  readonly swipeRight = output<void>();
  readonly swipeUp = output<void>();
  readonly swipeDown = output<void>();

  #startX = 0;
  #startY = 0;
  #endX = 0;
  #endY = 0;

  #isScrolling = false;
  #elementRef = inject(ElementRef);
  #scrollableElement: HTMLElement | null = null;
  #allowSwipeDown = false;

  /**
   * Обработчик начала касания
   * @param event Событие касания
   */
  protected onTouchStart = (event: TouchEvent): void => {
    const touch = event.touches[0];
    this.#startX = touch.clientX;
    this.#startY = touch.clientY;

    this.#scrollableElement = this.#getScrollableElement(event.target as HTMLElement);
    this.#isScrolling = !!this.#scrollableElement;

    // Определяем, разрешать ли свайп
    this.#allowSwipeDown = this.#scrollableElement ? this.#scrollableElement.scrollTop <= 0 : true;
  };

  /**
   * Обработчик окончания касания
   * @param event Событие касания
   */
  protected onTouchEnd = (event: TouchEvent): void => {
    const touch = event.changedTouches[0];
    this.#endX = touch.clientX;
    this.#endY = touch.clientY;

    const diffX = this.#endX - this.#startX;
    const diffY = this.#endY - this.#startY;

    const isHorizontal = Math.abs(diffX) > Math.abs(diffY);
    const threshold = this.swipeThreshold();

    // Горизонтальный свайп
    if (isHorizontal && Math.abs(diffX) > threshold) {
      diffX > 0 ? this.swipeRight.emit() : this.swipeLeft.emit();
    }
    // Вертикальный свайп
    else if (!isHorizontal && Math.abs(diffY) > threshold && (this.#allowSwipeDown || !this.#isScrolling)) {
      diffY > 0 ? this.swipeDown.emit() : this.swipeUp.emit();
    }

    // Сбрасываем состояния
    this.#isScrolling = false;
    this.#scrollableElement = null;
    this.#allowSwipeDown = false;
  };

  /**
   * Инициализация директивы
   */
  ngOnInit(): void {
    // Добавляем пассивные слушатели событий
    this.#elementRef.nativeElement.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.#elementRef.nativeElement.addEventListener('touchend', this.onTouchEnd);
  }

  /**
   * Очистка ресурсов при уничтожении директивы
   */
  ngOnDestroy(): void {
    // Удаляем слушатели событий
    this.#elementRef.nativeElement.removeEventListener('touchstart', this.onTouchStart);
    this.#elementRef.nativeElement.removeEventListener('touchend', this.onTouchEnd);
  }

  /**
   * Находит скроллируемый дочерний элемент, на котором началось касание
   * @returns HTMLElement если найден скроллируемый элемент, иначе null
   * @param target
   */
  #getScrollableElement(target: HTMLElement): HTMLElement | null {
    while (target && target !== this.#elementRef.nativeElement) {
      const style = getComputedStyle(target);
      const overflowY = style.overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && target.scrollHeight > target.clientHeight) {
        return target;
      }
      target = target.parentElement!;
    }
    return null;
  }
}
